import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { adminCreateBike, adminUpdateBike, fetchBike } from "@/lib/api";
import ImageUpload from "@/components/dashboard/ImageUpload";
import { toast } from "sonner";
import { ArrowLeft, Plus, X, FloppyDisk } from "@phosphor-icons/react";

const CATEGORIES = ["SuperSport", "Naked", "Adventure", "Cruiser", "Cafe Racer", "Sport Touring", "Scooter"];
const CONDITIONS = ["New", "Used"];
const COLLECTIONS = ["", "Thrust", "F.B. Mondial", "Asus", "RDB Electric"];

const empty = {
  brand: "",
  model: "",
  year: new Date().getFullYear(),
  category: "SuperSport",
  price: 0,
  mileage_km: 0,
  engine_cc: 0,
  power_hp: 0,
  weight_kg: 0,
  color: "",
  condition: "New",
  collection: "",
  image_url: "",
  gallery: [],
  description: "",
  featured: false,
  coming_soon: false,
};

export default function BikeForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    fetchBike(id).then((b) => {
      setForm({ ...empty, ...b, gallery: b.gallery || [] });
      setLoading(false);
    }).catch(() => {
      toast.error("Δεν βρέθηκε η μηχανή");
      navigate("/dashboard/bikes");
    });
  }, [id, isEdit, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNum = (k) => (e) => set(k, e.target.value === "" ? 0 : Number(e.target.value));
  const setStr = (k) => (e) => set(k, e.target.value);

  const addGalleryUrl = (url) => {
    if (!url) return;
    set("gallery", [...form.gallery, url]);
  };

  const removeGallery = (i) => {
    set("gallery", form.gallery.filter((_, idx) => idx !== i));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.brand || !form.model || !form.image_url) {
      toast.error("Συμπλήρωσε υποχρεωτικά πεδία (Μάρκα, Μοντέλο, Κύρια εικόνα)");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.collection === "") payload.collection = null;
      if (isEdit) {
        await adminUpdateBike(id, payload);
        toast.success("Αποθηκεύτηκε");
      } else {
        await adminCreateBike(payload);
        toast.success("Δημιουργήθηκε");
      }
      navigate("/dashboard/bikes");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = "Σφάλμα";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail
          .map((d) => {
            const field = Array.isArray(d.loc) ? d.loc.slice(-1)[0] : "";
            return field ? `${field}: ${d.msg}` : d.msg;
          })
          .join(" · ");
      }
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-white/50">Φόρτωση...</div>;

  return (
    <div data-testid="bike-form-page" className="space-y-8 max-w-4xl">
      <div className="flex items-center justify-between gap-4">
        <div>
          <Link to="/dashboard/bikes" data-testid="back-to-bikes" className="text-overline inline-flex items-center gap-2 hover:text-[#E50000]">
            <ArrowLeft size={12} weight="bold" /> Πίσω στις μηχανές
          </Link>
          <h1 className="mt-3 font-display font-black uppercase text-4xl tracking-tight">
            {isEdit ? "Επεξεργασία μηχανής" : "Νέα μηχανή"}
          </h1>
        </div>
      </div>

      <form data-testid="bike-form" onSubmit={onSubmit} className="space-y-8 border border-white/10 bg-[#141414] p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Μάρκα *">
            <input data-testid="bike-brand" required value={form.brand} onChange={setStr("brand")} className="dash-input" placeholder="Ducati" />
          </Field>
          <Field label="Μοντέλο *">
            <input data-testid="bike-model" required value={form.model} onChange={setStr("model")} className="dash-input" placeholder="Panigale V4" />
          </Field>
          <Field label="Έτος">
            <input data-testid="bike-year" type="number" value={form.year} onChange={setNum("year")} className="dash-input" />
          </Field>
          <Field label="Χρώμα">
            <input data-testid="bike-color" value={form.color} onChange={setStr("color")} className="dash-input" />
          </Field>
          <Field label="Κατηγορία">
            <select data-testid="bike-category" value={form.category} onChange={setStr("category")} className="dash-input">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Κατάσταση">
            <select data-testid="bike-condition" value={form.condition} onChange={setStr("condition")} className="dash-input">
              {CONDITIONS.map((c) => <option key={c} value={c}>{c === "New" ? "Καινούργιο" : "Μεταχειρισμένο"}</option>)}
            </select>
          </Field>
          <Field label="Σειρά / Κατάλογος">
            <select data-testid="bike-collection" value={form.collection || ""} onChange={setStr("collection")} className="dash-input">
              {COLLECTIONS.map((c) => <option key={c} value={c}>{c || "— Χωρίς σειρά —"}</option>)}
            </select>
          </Field>
          <Field label="Τιμή (€)">
            <input data-testid="bike-price" type="number" value={form.price} onChange={setNum("price")} className="dash-input" />
          </Field>
          <Field label="Χιλιόμετρα">
            <input data-testid="bike-mileage" type="number" value={form.mileage_km} onChange={setNum("mileage_km")} className="dash-input" />
          </Field>
          <Field label="Κυβικά (cc)">
            <input data-testid="bike-cc" type="number" value={form.engine_cc} onChange={setNum("engine_cc")} className="dash-input" />
          </Field>
          <Field label="Ίπποι (hp)">
            <input data-testid="bike-hp" type="number" value={form.power_hp} onChange={setNum("power_hp")} className="dash-input" />
          </Field>
          <Field label="Βάρος (kg)">
            <input data-testid="bike-weight" type="number" value={form.weight_kg} onChange={setNum("weight_kg")} className="dash-input" />
          </Field>
          <Field label="Featured (στην αρχική)">
            <label className="flex items-center gap-2 h-[48px]">
              <input data-testid="bike-featured" type="checkbox" checked={!!form.featured} onChange={(e) => set("featured", e.target.checked)} className="w-5 h-5 accent-[#E50000]" />
              <span className="text-sm text-white/70">Προβολή στην αρχική</span>
            </label>
          </Field>
          <Field label="Coming Soon">
            <label className="flex items-center gap-2 h-[48px]">
              <input data-testid="bike-coming-soon" type="checkbox" checked={!!form.coming_soon} onChange={(e) => set("coming_soon", e.target.checked)} className="w-5 h-5 accent-[#E50000]" />
              <span className="text-sm text-white/70">Εμφάνιση ως «Coming Soon»</span>
            </label>
          </Field>
        </div>

        <Field label="Περιγραφή">
          <textarea data-testid="bike-description" rows={4} value={form.description} onChange={setStr("description")} className="dash-input resize-none" />
        </Field>

        <ImageUpload
          testid="bike-main-image"
          label="Κύρια εικόνα *"
          value={form.image_url}
          onChange={(url) => set("image_url", url)}
          accept="image/*"
        />

        <div className="space-y-3">
          <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">Galleria</span>
          <div className="flex flex-wrap gap-3">
            {form.gallery.map((g, i) => (
              <div key={i} className="relative w-24 h-24 border border-white/15">
                <img src={g} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeGallery(i)} data-testid={`gallery-remove-${i}`} className="absolute top-1 right-1 w-6 h-6 bg-black/70 border border-white/20 flex items-center justify-center hover:bg-[#E50000]">
                  <X size={12} weight="bold" />
                </button>
              </div>
            ))}
          </div>
          <ImageUpload
            testid="bike-gallery-add"
            label="Προσθήκη εικόνας"
            value=""
            onChange={addGalleryUrl}
            accept="image/*"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
          <Link to="/dashboard/bikes" className="btn-outline text-xs">Ακύρωση</Link>
          <button data-testid="bike-save-btn" type="submit" disabled={saving} className="btn-primary text-xs disabled:opacity-50">
            <FloppyDisk size={14} weight="bold" className="mr-2" />
            {saving ? "Αποθήκευση..." : "Αποθήκευση"}
          </button>
        </div>
      </form>

      <style>{`
        .dash-input {
          width: 100%;
          background: #1A1A1A;
          border: 1px solid rgba(255,255,255,0.15);
          color: white;
          padding: 0.75rem 0.9rem;
          font-family: 'Manrope', sans-serif;
          font-size: 0.9rem;
          outline: none;
          border-radius: 2px;
        }
        .dash-input:focus { border-color: #E50000; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-white/60 mb-2">{label}</span>
      {children}
    </label>
  );
}
